import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import {
  Badge,
  type BadgeVariant,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  Skeleton,
} from '@beamx/shared';
import { messages, type MessageStatus } from '../lib/api/messages.js';

const STATUS_VARIANT: Record<MessageStatus, BadgeVariant> = {
  Sent: 'success',
  Sending: 'default',
  Accepted: 'default',
  Failed: 'destructive',
};

export function MessageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const message = useQuery({ queryKey: ['message', id], queryFn: () => messages.get(id!), enabled: !!id });
  const events = useQuery({ queryKey: ['message-events', id], queryFn: () => messages.events(id!), enabled: !!id });

  if (!id) return null;

  return (
    <>
      <Link to="/activity" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />
        Activity
      </Link>

      {message.isLoading ? (
        <div className="space-y-4"><Skeleton className="h-9 w-80" /><Skeleton className="h-40 w-full" /></div>
      ) : message.isError ? (
        <Card><CardContent className="p-6 text-sm text-destructive">{(message.error as Error)?.message ?? 'Failed to load message.'}</CardContent></Card>
      ) : message.data ? (
        <>
          <PageHeader
            title={message.data.subject}
            description={`${message.data.fromEmail} → ${message.data.toEmail}`}
            actions={<Badge variant={STATUS_VARIANT[message.data.status]} dot>{message.data.status}</Badge>}
          />

          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle className="text-base">Rendered HTML</CardTitle><CardDescription>What an HTML-capable client shows.</CardDescription></CardHeader>
              <CardContent>
                <iframe
                  sandbox=""
                  className="h-[480px] w-full rounded-md border bg-white"
                  srcDoc={message.data.bodyHtml}
                  title="Rendered HTML body"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Timeline</CardTitle></CardHeader>
              <CardContent>
                {events.isLoading ? (
                  <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
                ) : events.data && events.data.length > 0 ? (
                  <ol className="space-y-2">
                    {events.data.map((e) => (
                      <li key={e.id} className="rounded-md border px-3 py-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{e.eventType}</span>
                          <time className="text-xs text-muted-foreground">{new Date(e.occurredAt).toLocaleString()}</time>
                        </div>
                        {(e.detail || e.receiverHost) && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {e.receiverHost && <span>via {e.receiverHost} · </span>}{e.detail}
                          </p>
                        )}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="py-4 text-center text-sm text-muted-foreground">No events recorded yet.</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="mt-6">
            <CardHeader><CardTitle className="text-base">Metadata</CardTitle></CardHeader>
            <CardContent>
              <dl className="grid grid-cols-3 gap-y-2 text-sm">
                <Meta label="Message ID" mono>{message.data.id}</Meta>
                <Meta label="Template">{message.data.templateKey ?? '— (inline send)'}</Meta>
                <Meta label="Accepted">{new Date(message.data.acceptedAt).toLocaleString()}</Meta>
                {message.data.sentAt && <Meta label="Sent">{new Date(message.data.sentAt).toLocaleString()}</Meta>}
                {message.data.failedAt && <Meta label="Failed">{new Date(message.data.failedAt).toLocaleString()}</Meta>}
                {message.data.failureReason && <Meta label="Failure">{message.data.failureReason}</Meta>}
                {message.data.smtpMessageId && <Meta label="SMTP Message-ID" mono>{message.data.smtpMessageId}</Meta>}
                {message.data.clientMessageId && <Meta label="client_message_id" mono>{message.data.clientMessageId}</Meta>}
                {message.data.categories && <Meta label="Categories">{message.data.categories}</Meta>}
              </dl>

              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-foreground">Plaintext fallback</summary>
                <pre className="mt-2 overflow-x-auto rounded-md bg-muted/50 p-3 text-xs">{message.data.bodyText}</pre>
              </details>
            </CardContent>
          </Card>
        </>
      ) : null}
    </>
  );
}

function Meta({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`col-span-2 font-medium ${mono ? 'font-mono text-xs break-all' : ''}`}>{children}</dd>
    </>
  );
}
